import { Component, Input, OnInit } from '@angular/core';
import { EChartsOption, graphic } from 'echarts';
import * as _ from 'lodash';
import { Subscription } from 'rxjs';
import { FiltersService } from 'src/app/core/services/filters.service';
import { ServiceInsightsService } from '../../services/service-insights-service.service';
import { PestServiceInsightsCallData, ServiceInsightRecord, ServiceInsightsCallData, ServiceInsightsObservationsData, ServiceMetricTypes, SICallTypes } from '../../types/service-insight-types';
import { ContractModel } from 'src/app/global/contract-filter-model';
import { MixpanelService } from 'src/app/core/services/mixpanel.service';
import { ServiceCustomerFilterOptions } from 'src/app/global/service-customer-model';
import { Division } from 'src/app/core/services/ebr-api-client/ebr-api-client.g';

// TODO: Repurpose and refactor this component for Pest Service Insights only which needs metrics and graph.
// Use ServiceMetricCardComponent for Service Insights metrics without graph.
@Component({
    selector: 'service-metric-line-chart',
    templateUrl: './service-metric-line-chart.component.html',
    styleUrls: ['./service-metric-line-chart.component.scss'],
    standalone: false
})
export class ServiceMetricLineChartComponent implements OnInit {
  @Input() topBorderColor: string = '#3290ED'; //default to blue
  @Input() title: string = 'Title';
  @Input() serviceType: ServiceMetricTypes = ServiceMetricTypes.Calls;
  @Input() key : string = '';
  @Input() division = 'Contract';
  @Input() isEnableCheckBox: boolean = false;

  loader: boolean = true;

  filters: ContractModel | undefined;

  selectAllSubscription: Subscription | undefined;

  checked: boolean = false;

  chartOptions: EChartsOption = {};
  data: ServiceInsightRecord[] | undefined;

  totalCount: number = 0;

  growth: number | null = null;
  growthPositive: boolean = true;
  growthDateRangeAvailable : boolean = false;

  initialized: boolean = false;
  id: string = '';

  selectedAdvancedFilters: ServiceCustomerFilterOptions = {
    "accountNumber": [],
    "accountName": [],
    "country": [],
    "state": [],
    "city": [],
    "zipCode": [],
    "recurringIssue": [],
   };

  serviceCustomerFiltersSubscription: Subscription| undefined;

  Division = Division;

  constructor(
    private _filterService: FiltersService,
    private _serviceInsightsService: ServiceInsightsService,
    private _mixpanelService: MixpanelService
  ) {}

  isEmptyData(data: ServiceInsightRecord[]) {
    return data.length == 0;
  }

  findingsMetrics: Map<string, string> = new Map([
    ['Pest', 'Pest Findings'],
    ['Sanitation', 'Sanitation Findings'],
    ['Structural', 'Structural Findings'],
    ['Prep', 'Prep Findings'],
    ['ESR','Pest ESR']
  ]);

  async ngOnInit() {
    this.selectedAdvancedFilters = {
      "accountNumber": [],
      "accountName": [],
      "country": [],
      "state": [],
      "city": [],
      "zipCode": [],
      "recurringIssue": [],
     };

    // Get the filters selected
    this._filterService.selectedFiltersObservable.subscribe(f => this.filters = f);

    if (this.filters != undefined) {
      this.id = this.title.replace(/\s+/g, '-').replace(/-+/g, '-').toLowerCase().trim();
      this.selectAllSubscription = this._serviceInsightsService.isAllSelected.subscribe((allSelected) => {
        this.checked = allSelected;
        this._onCheckedChange(this.checked);
      });

      this.loader = true;

      await this.loadData();
      this.growthDateRangeAvailable = this._filterService.IsPreviousPeriodAvailable();
      //Added this condition because no advance filters are available for Pest
      if(this.division != 'Pest'){
        this.serviceCustomerFiltersSubscription = this._serviceInsightsService.getServiceCustomerFilters().subscribe(async advFilter => {
          this.selectedAdvancedFilters = advFilter;
        });
      }

      this.loader = false;
    }

  }

  async loadData() {
    if(this.division === 'Pest') {
      this.subscribeToPestServiceData();
      await this.loadPestServiceData();
    } else {
      this.subscribeToServiceData();
      await this.loadServiceData();
    }

    if ((this.data != undefined && !this.isEmptyData(this.data))) {
      this.setUpData();
    }
  }

  subscribeToPestServiceData() {
    this._serviceInsightsService.getPestServiceInsightsCallData(this.key as keyof PestServiceInsightsCallData).subscribe(
      (d) => {
        this.data = d.periods;
        this.growth = d.percentChangeInCount;
      }
    );
  }

  subscribeToServiceData() {
    if (this.serviceType == ServiceMetricTypes.Calls) {
      this._serviceInsightsService.getServiceInsightsCallData(this.key as keyof ServiceInsightsCallData).subscribe( (d) => {
          this.data = d.periods;
          this.growth = d.percentChangeInCount; 
        }
      );
    } else {
      this._serviceInsightsService.getServiceInsightsObsData(this.key as keyof ServiceInsightsObservationsData).subscribe( (d) => {
          this.data = d?.periods;
          this.growth = d?.percentChangeInCount ?? null
        }
      );
    }
  }

  async loadPestServiceData() {
    if (this.data != undefined && this.isEmptyData(this.data)) {
      this._serviceInsightsService.setPestServiceInsightsCallData(this.key as keyof PestServiceInsightsCallData, //should fail
        await this._serviceInsightsService.getSIMetricByPeriodFromAPI(this.serviceType, this.key as SICallTypes, this.selectedAdvancedFilters)
      )
      this.trackMixpanelEvent();
    }
  }

  async loadServiceData() {
    if (this.serviceType == ServiceMetricTypes.Calls) {
      if (this.data != undefined && this.isEmptyData(this.data)) {
        this._serviceInsightsService.setServiceInsightsCallData(this.key as keyof ServiceInsightsCallData,
          await this._serviceInsightsService.getSIMetricByPeriodFromAPI(this.serviceType, this.key as SICallTypes, this.selectedAdvancedFilters)
        )
        this.trackMixpanelEvent();
      }
    } else {
      if (this.data != undefined && this.isEmptyData(this.data)) {
        this._serviceInsightsService.setServiceInsightsObsData(
          this.key as keyof ServiceInsightsObservationsData,
          await this._serviceInsightsService.getSIMetricByPeriodFromAPI(this.serviceType, this.key as SICallTypes, this.selectedAdvancedFilters)
        )
      }
    }
  }

  private trackMixpanelEvent() {
    this._mixpanelService.track('Service Insights- ' + this.key + ' Loaded', {
      GraphType: this.key,
      ServiceType: this.serviceType
    });
  }

  setUpData() {
    this.totalCount = this.data!.reduce(
      (sum, current) => sum + current.total,
      0
    ); //getting total count across all days in this period

    this.setOptions();
    this.setGrowth();
    this._onCheckedChange(this.checked);
  }

  setOptions() {
    this.chartOptions = {
      grid: {
        //since we are not displaying the axis labels or legend, let graph take up entirety of canvas
        left: 0,
        right: 0,
        bottom: 0,
        top: 0,
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: this.data?.map((record) => record.endDate),
        show: false,
      },
      yAxis: {
        type: 'value',
        show: false,
      },
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => {
          const serviceUnit = this.serviceType == ServiceMetricTypes.CallsByReason ? 'Calls' : this.serviceType;
          let date = new Date(params[0].data.date);
          return `
              <div style="display:flex; align-items:bottom; gap:8px; ">
                <span class="total-calls-tooltip" style="font-size:30px; color:#313233;">${
                  params[0].data.value
                }</span><span style="font-size:12px; color:#8E9393">total ${serviceUnit}</span>
              </div>
              <br/>
              <span style="color:#313233; font-weight=400; font-family:Roboto;"> ${date.toLocaleDateString(
                'en-US',
                { month: 'long', day: 'numeric', year: 'numeric' }
              )}</span><br/>
              across ${params[0].data.totalLocations} locations
              `;
        },
      },
      lineStyle: {
        color: '#3AA3E2',
      },
      series: [
        {
          data: this.data?.map((record) => {
            return {
              value: record.total,
              date: record.endDate,
              totalLocations: record.totalLocations,
            };
          }),
          type: 'line',
          symbol: 'none',
          connectNulls: true,
          areaStyle: {
            color: new graphic.LinearGradient(0, 0, 0, 1, [
              {
                offset: 0,
                color: 'rgba(26, 165, 224, 0.6)',
              },
              {
                offset: 1,
                color: 'rgba(26, 165, 224, 0)',
              },
            ]),
          },
        },
      ],
    };
  }


  hasFindingsMetrics(callType: string): boolean {
    if(callType === 'ESR')
      return false;
    else
    return Array.from(this.findingsMetrics.keys()).includes(callType);
  }
  //function that runs when the checkbox is clicked
  _onCheckedChange(b: boolean) {
    this.checked = b;
    if (this.totalCount != undefined) {
      if (this.checked && this.isEnableCheckBox) {
        this._serviceInsightsService.addMetricToPresentation(
          this.getMetricName(this.title),
          this.totalCount
        );
      } else {
        this._serviceInsightsService.removeMetricFromPresentation(this.getMetricName(this.title));
      }
    }
    this._serviceInsightsService.setPestMetricsSelected(this.title, this.checked);
  }

  private getMetricName(key:string){
    return this.findingsMetrics.get(key) || key;
  }

  setGrowth() {
    if (this.growth !== null) {
      this.growthPositive = this.growth >= 0;
      this.growth = Math.abs(this.growth);
    }
  }

  ngOnDestroy() {
    this.selectAllSubscription?.unsubscribe();
    this.serviceCustomerFiltersSubscription?.unsubscribe();
  }
}
